<template>
  <div class="nouvelle-etude-wrapper">
    <!-- Barre de progression horizontale (masquée sur step 1 pour ne pas couvrir la carte) -->
    <div class="wizard-progress" :class="{ 'wizard-progress--transparent': currentStep === 1 }">
      <div class="wizard-steps-bar">
        <div
          v-for="(step, idx) in steps"
          :key="idx"
          class="wizard-step-item"
          :class="{
            'wizard-step-item--done': accessible(idx + 1),
            'wizard-step-item--active': currentStep === idx + 1,
            'wizard-step-item--clickable': accessible(idx + 1),
          }"
          @click="goToStep(idx + 1)"
        >
          <div class="wizard-step-bubble">
            <span v-if="accessible(idx + 1)">✓</span>
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <span class="wizard-step-label">{{ step.label }}</span>
          <div v-if="idx < steps.length - 1" class="wizard-step-connector"></div>
        </div>
      </div>
    </div>

    <!-- Contenu de l'étape courante -->
    <div class="wizard-body">
      <div v-if="draftLoading" class="wizard-loading">
        <div class="wizard-loading__spinner"></div>
        <p>Chargement de l'étude...</p>
      </div>
      <template v-else>
        <step1-projet
          v-if="currentStep === 1"
          @next="goNext"
          @cancel="goHome"
        />
        <step2-toiture
          v-if="currentStep === 2"
          @next="goNext"
          @back="goBack"
        />
        <step3-consommation
          v-if="currentStep === 3"
          @next="goNext"
          @back="goBack"
          @save-draft="saveDraft"
        />
        <step4-parametres
          v-if="currentStep === 4"
          @next="goNext"
          @back="goBack"
        />
        <step5-calcul
          v-if="currentStep === 5"
          @next="goNext"
          @back="goBack"
        />
        <step6-rapport
          v-if="currentStep === 6"
          @back="goBack"
          @restart="restart"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import axios from 'axios'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'
import Step1Projet from './wizard/Step1Projet.vue'
import Step2Toiture from './wizard/Step2Toiture.vue'
import Step3Consommation from './wizard/Step3Consommation.vue'
import Step4Parametres from './wizard/Step4Parametres.vue'
import Step5Calcul from './wizard/Step5Calcul.vue'
import Step6Rapport from './wizard/Step6Rapport.vue'

export default {
  name: 'NouvelleEtude',
  components: {
    Step1Projet,
    Step2Toiture,
    Step3Consommation,
    Step4Parametres,
    Step5Calcul,
    Step6Rapport,
  },
  data() {
    return { draftLoading: false }
  },
  created() {
    // Bloquer le rendu des steps tant que le draft n'est pas chargé
    if (this.$route && this.$route.query && this.$route.query.draftId) {
      this.draftLoading = true
    }
  },
  computed: {
    ...mapState('wizard', ['currentStep', 'etapeAtteinte', 'draftId', 'consumptionCurveId', 'calculStatus', 'matrixResults', 'dirty']),
    steps() {
      return [
        { label: 'Projet' },
        { label: 'Toiture' },
        { label: 'Consommation' },
        { label: 'Paramètres' },
        { label: 'Calcul' },
        { label: 'Rapport' },
      ]
    },
    hasResults() {
      return !!(this.matrixResults && this.matrixResults.length)
    },
  },
  methods: {
    ...mapMutations('wizard', ['SET_STEP', 'RESET', 'SET_DRAFT_ID', 'RESTORE_DRAFT', 'SET_CONSUMPTION_CURVE_ID', 'SET_CALCUL']),
    async goNext() {
      if (this.currentStep < 6) {
        if (this.currentStep <= 4) {
          if (this.dirty || !this.hasResults) {
            // Paramètres modifiés ou pas encore de résultats : sauvegarder + invalider le calcul
            await this.saveDraft(this.currentStep + 1)
            this.invaliderCalcul()
          }
        }
        this.SET_STEP(this.currentStep + 1)
      }
    },
    async goBack() {
      if (this.currentStep > 1) {
        const etape = this.currentStep - 1
        this.SET_STEP(etape)
        await this.enregistrerEtapeEnCours(etape)
      }
    },
    /**
     * Étude reprise sur l'étape en cours (12/10/2026) : enregistrée après le changement d'étape, une fois que l'étape
     * quittée a reporté ses champs dans le store. Étude calculée et non modifiée : rien à enregistrer.
     */
    async enregistrerEtapeEnCours(etape) {
      if (this.hasResults && !this.dirty) return
      await this.$nextTick()
      await this.saveDraft(etape)
    },
    /**
     * Étapes accessibles depuis la barre (12/10/2026) : toutes si l'étude est calculée et non modifiée ; sinon les étapes
     * précédentes et les suivantes déjà atteintes, jusqu'au calcul (le rapport attend un calcul à jour).
     */
    accessible(n) {
      if (n === this.currentStep || n < 1) return false
      if (this.hasResults && !this.dirty) return true
      return n < this.currentStep || n <= Math.min(this.etapeAtteinte || 1, 5)
    },
    /** Calcul fait avant une modification : effacé, la Step 5 propose de le relancer. */
    invaliderCalcul() {
      if (this.calculStatus === 'idle') return
      this.SET_CALCUL({ status: 'idle', progress: 0, message: '' })
      this.$store.commit('wizard/CLEAR_RESULTS')
    },
    async goToStep(stepNum) {
      if (!this.accessible(stepNum)) return
      // étude modifiée puis Step 5 rejointe par la barre : la Step 5 montrait « Calcul terminé » et l'ancien rapport
      // (passage de particulier à pro, 26/10/2026)
      if (this.dirty) this.invaliderCalcul()
      this.SET_STEP(stepNum)
      await this.enregistrerEtapeEnCours(stepNum)
    },
    goHome() {
      this.$router.push({ name: 'home' })
    },
    restart() {
      this.RESET()
      this.SET_STEP(1)
    },
    handleReset() {
      // Store déjà reset par Menu — forcer le remount des steps via draftLoading ; l'adresse ne doit plus désigner
      // l'étude précédente (sinon F5 la rouvrirait)
      if (window.location.search) {
        window.history.replaceState(window.history.state, '', window.location.pathname)
      }
      this.draftLoading = true
      this.$nextTick(() => { this.draftLoading = false })
    },
    /** `etapeEnCours` : étape sur laquelle l'étude rouvrira (12/10/2026 ; la dernière étape validée auparavant). */
    async saveDraft(etapeEnCours) {
      try {
        const token = authenticationService.status.jwtToken
        const state = this.$store.state.wizard
        const skip = new Set(['csvFile', 'simulationResult', 'resultSansBatterie', 'resultAvecBatterie',
                              'matrixResults', 'selectedMatrixCell', 'calculStatus', 'calculProgress', 'calculMessage', 'recalculAuto'])
        const serializable = {}
        Object.keys(state).forEach(k => { if (!skip.has(k)) serializable[k] = state[k] })
        if (etapeEnCours) serializable.etapeAtteinte = Math.max(state.etapeAtteinte || 1, etapeEnCours)

        const payload = {
          id: state.draftId || null,
          projectName: state.projectName || '',
          status: 'DRAFT',
          consumptionCurveId: state.consumptionCurveId || null,
          stepReached: etapeEnCours || state.currentStep,
          wizardJson: JSON.stringify(serializable),
        }
        const response = await axios.post(
          conf.API_URL + '/wizard/draft',
          payload,
          { headers: { Authorization: 'Bearer ' + token } }
        )
        const saved = response.data && response.data.data
        if (saved && saved.id) {
          this.SET_DRAFT_ID(saved.id)
          // identifiant du brouillon dans l'adresse : F5 rouvre l'étude en cours (B43). Pas de navigation du routeur :
          // App.vue recrée la page à chaque changement d'adresse (router-view :key="fullPath"), ce qui relancerait
          // l'assistant en pleine saisie ; l'adresse est donc réécrite directement dans l'historique du navigateur.
          const adresse = window.location.pathname + '?draftId=' + saved.id
          if (window.location.pathname + window.location.search !== adresse) {
            window.history.replaceState(window.history.state, '', adresse)
          }
        }
      } catch (e) {
        // Auto-save silencieux — ne pas bloquer la navigation
      }
    },
    async resumeFromDraft(draftId, editMode = false) {
      try {
        const token = authenticationService.status.jwtToken
        const headers = { Authorization: 'Bearer ' + token }
        const response = await axios.get(conf.API_URL + '/wizard/draft/' + draftId, { headers })
        const draft = response.data && response.data.data
        if (draft && draft.wizardJson) {
          const state = JSON.parse(draft.wizardJson)
          this.RESTORE_DRAFT(state)
          if (draft.consumptionCurveId) {
            this.SET_CONSUMPTION_CURVE_ID({ id: draft.consumptionCurveId, status: 'saved' })
          }
          this.SET_DRAFT_ID(draft.id)

          // Restaurer les résultats depuis la table dédiée (Option B)
          if (draft.hasResult) {
            try {
              const resResponse = await axios.get(
                conf.API_URL + '/wizard/draft/' + draftId + '/result',
                { headers }
              )
              const resData = resResponse.data && resResponse.data.data
              if (resData) {
                this.$store.commit('wizard/SET_RESULT', {
                  sansBatterie: resData.sansBatterieJson ? JSON.parse(resData.sansBatterieJson) : null,
                  avecBatterie: resData.avecBatterieJson ? JSON.parse(resData.avecBatterieJson) : null,
                  matrix: resData.matrixJson ? JSON.parse(resData.matrixJson) : null,
                  availableBatteryKwh: state.availableBatteryKwh || null,
                })
              }
            } catch (re) {
              console.warn('Résultats non restaurables:', re)
            }
          } else if (state.resultSansBatterie || state.resultAvecBatterie) {
            // Fallback : résultats encore dans le wizardJson (ancien comportement)
            this.$store.commit('wizard/SET_RESULT', {
              sansBatterie: state.resultSansBatterie || null,
              avecBatterie: state.resultAvecBatterie || null,
              matrix: state.matrixResults || null,
              availableBatteryKwh: state.availableBatteryKwh || null,
            })
          }

          // editMode → reprend à l'étape 1 ; étude complétée → directement Step 6
          let targetStep = editMode ? 1 : (draft.status === 'COMPLETED' && draft.hasResult ? 6 : (draft.stepReached || 1))
          // étude terminée de l'ancienne version, prix ramenés en HT (v6) : rapport recalculé aussitôt (Step 5)
          if (this.$store.state.wizard.recalculAuto && targetStep === 6) targetStep = 5
          else this.$store.commit('wizard/SET_RECALCUL_AUTO', false)
          // étapes déjà atteintes : accessibles depuis la barre, même en reprenant à l'étape 1
          this.$store.commit('wizard/SET_ETAPE_ATTEINTE', Math.max(state.etapeAtteinte || 1, draft.stepReached || 1, targetStep))
          this.SET_STEP(targetStep)
        }
      } catch (e) {
        console.warn('Impossible de reprendre le draft:', e)
      }
    },
  },
  async mounted() {
    this.$root.$on('nouvelle-etude-reset', this.handleReset)
    const query = this.$route && this.$route.query
    const draftId = query && query.draftId
    const editMode = query && query.mode === 'edit'
    if (draftId) {
      await this.resumeFromDraft(draftId, editMode)
    }
    this.draftLoading = false
  },
  beforeDestroy() {
    this.$root.$off('nouvelle-etude-reset', this.handleReset)
  },
}
</script>

<style scoped>
.nouvelle-etude-wrapper {
  font-family: 'Raleway', sans-serif;
  min-height: calc(100vh - 80px);
}

/* Barre de progression */
.wizard-progress {
  background: #fff;
  border-bottom: 1px solid #eee;
  padding: 0 32px;
  position: relative;
  z-index: 500;
}

.wizard-progress--transparent {
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: none;
}

.wizard-steps-bar {
  display: flex;
  align-items: center;
  height: 60px;
  max-width: 900px;
  margin: 0 auto;
}

.wizard-step-item {
  display: flex;
  align-items: center;
  flex: 1;
  position: relative;
}

.wizard-step-bubble {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ddd;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s, transform 0.15s;
}

.wizard-step-item--active .wizard-step-bubble {
  background: #f5c518;
  color: #222;
  box-shadow: 0 0 0 3px rgba(245, 197, 24, 0.25);
}

.wizard-step-item--done .wizard-step-bubble {
  background: #4caf50;
  color: #fff;
}

.wizard-step-label {
  margin-left: 8px;
  font-size: 13px;
  color: #aaa;
  white-space: nowrap;
  font-weight: 500;
}

.wizard-step-item--active .wizard-step-label {
  color: #222;
  font-weight: 700;
}

.wizard-step-item--done .wizard-step-label {
  color: #4caf50;
}

.wizard-step-item--clickable {
  cursor: pointer;
}

.wizard-step-item--clickable:hover .wizard-step-bubble {
  background: #3d9140;
  transform: scale(1.1);
  transition: background 0.15s, transform 0.15s;
}

.wizard-step-item--clickable:hover .wizard-step-label {
  color: #2e7d32;
}

.wizard-step-connector {
  flex: 1;
  height: 2px;
  background: #ddd;
  margin: 0 10px;
  min-width: 20px;
}

.wizard-step-item--done .wizard-step-connector {
  background: #4caf50;
}

/* Corps du wizard */
.wizard-body {
  position: relative;
}

.wizard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
  color: #888;
  font-size: 14px;
}

.wizard-loading__spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #f5c518;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }
</style>
